import { useEffect, useMemo } from "react";
import { Candidate, Column } from "../interface";
import { useGetCandidatesByColumnId } from "./api";
import { useBoard } from "../contexts/board.context";

export const useColumn = (column: Column) => {
  const { moves, moveCandidate, search, filters } = useBoard();

  const { data, isError, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useGetCandidatesByColumnId(column.id, search, filters);

  // Column content = server pages for this column, minus cards optimistically moved away,
  // plus cards optimistically moved in from another column.
  const candidates = useMemo<Candidate[]>(() => {
    const fetched = data?.pages.flatMap((page) => page.candidates ?? []) ?? [];
    const fetchedIds = new Set(fetched.map((candidate) => candidate.id));

    const own = fetched
      .filter((candidate) => !moves[candidate.id] || moves[candidate.id].to === column.id)
      .map((candidate) => (moves[candidate.id] ? { ...candidate, ...moves[candidate.id].patch } : candidate));

    const incoming = Object.values(moves)
      .filter((move) => move.to === column.id && !fetchedIds.has(move.candidate.id))
      .map((move) => ({ ...move.candidate, ...move.patch }));

    const list = [...incoming, ...own];
    if (column.id === 1) {
      return list.sort((a, b) => (new Date(a.created_at).getTime() < new Date(b.created_at).getTime() ? 1 : -1));
    }
    return list;
  }, [data, moves, column.id]);

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, fetchNextPage, isFetchingNextPage]);

  return {
    candidates,
    moveCandidate,
    isError,
    isLoading,
    hasNextPage,
  };
};
